import { useEffect, useState } from "react";
import { api, detailText } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/context/LanguageContext";

const CATS = ["general","meeting","fitting","payment","rehearsal","setup","ceremony","reception"];
const EMPTY_FORM = { title: "", date: "", start_time: "", end_time: "", location: "", category: "general", notes: "" };

export default function Timeline() {
  const { t } = useT();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  // null = mode tambah; berisi event_id = mode edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => api.get("/timeline").then(({ data }) => setEvents(data.events ?? [])).catch((e) => {
    toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat rundown acara");
  });
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const startEdit = (e) => {
    setEditingId(e.event_id);
    setForm({
      title: e.title || "",
      date: e.date || "",
      start_time: e.start_time || "",
      end_time: e.end_time || "",
      location: e.location || "",
      category: e.category || "general",
      notes: e.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.date) return toast.error(t("time.required"));
    try {
      if (editingId) {
        await api.patch(`/timeline/${editingId}`, form);
        toast.success(t("time.updated"));
      } else {
        await api.post("/timeline", form);
        toast.success(t("time.added"));
      }
      setOpen(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || (editingId ? "Gagal menyimpan perubahan" : "Gagal menambah acara"));
    }
  };
  const remove = async (e) => {
    if (!window.confirm("Hapus acara ini dari jadwal?")) return;
    try {
      await api.delete(`/timeline/${e.event_id}`);
      load();
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal menghapus acara");
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div><div className="text-xs uppercase tracking-widest text-muted-foreground">{t("time.section")}</div><h1 className="font-serif text-4xl">{t("time.title")}</h1></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="rundown-add-row-btn" className="rounded-full" onClick={openAdd}><Plus className="h-4 w-4 mr-1" /> {t("time.add_btn")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? t("time.edit") : t("time.new")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("time.title_field")}</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t("common.date")}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><Label>{t("time.start")}</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                <div><Label>{t("time.end")}</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("time.location")}</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                <div><Label>{t("common.category")}</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <Button onClick={save} className="w-full rounded-full" data-testid="save-event-btn">{t("time.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {events.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground rounded-2xl">{t("time.empty")}</Card>
      ) : (
        <Card className="rounded-2xl overflow-hidden">
          <ul className="divide-y divide-border">
            {events.map((e) => (
              <li key={e.event_id} className="p-4 flex items-center gap-4" data-testid="rundown-table-row">
                <div className="w-32 shrink-0">
                  <div className="font-serif text-lg">{e.date}</div>
                  <div className="text-xs text-muted-foreground">{e.start_time}{e.end_time && ` – ${e.end_time}`}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{e.title}</div>
                  <div className="text-xs text-muted-foreground">{e.location}{e.category && ` · ${e.category}`}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(e)} title={t("time.edit")} data-testid={`rundown-edit-${e.event_id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(e)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
